import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquare, Pin, BookOpen, Users, Shield, Layers } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useHasRole } from "@/hooks/useUserRoles";
import { formatDistanceToNow } from "date-fns";
import { DiscussionThread } from "@/components/lms/DiscussionThread";
import { GeneralDiscussionList } from "@/components/lms/GeneralDiscussionList";
import { MentorDiscussionList } from "@/components/lms/MentorDiscussionList";
import { CohortDiscussionThread } from "@/components/discussions/CohortDiscussionThread";
import { useMyCohortDiscussions } from "@/hooks/useCohortDiscussions";
import { Link } from "react-router-dom";
import { UserName } from "@/components/badges/UserName";

interface DiscussionWithCourse {
  id: string;
  title: string;
  content: string;
  is_pinned: boolean | null;
  created_at: string;
  course_id: string;
  course_title: string;
  user_id: string;
  author: {
    full_name: string;
    avatar_url: string | null;
  } | null;
  reply_count: number;
}

export default function Discussions() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const isAdmin = useHasRole("admin");
  const isMentor = profile?.role === "mentor";
  const canSeeMentorTab = isAdmin || isMentor;
  const [selectedDiscussion, setSelectedDiscussion] = useState<string | null>(null);
  const [selectedCohortDiscussion, setSelectedCohortDiscussion] = useState<{
    id: string;
    cohortId: string;
  } | null>(null);
  const { data: cohortDiscussions, isLoading: cohortLoading } = useMyCohortDiscussions(user?.id);
  const { data: discussions, isLoading } = useQuery({
    queryKey: ["all-course-discussions", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get enrolled course IDs
      const { data: enrollments } = await supabase
        .from("course_enrollments")
        .select("course_id")
        .eq("user_id", user.id);

      if (!enrollments || enrollments.length === 0) return [];

      const courseIds = enrollments.map((e) => e.course_id);

      // Fetch discussions for enrolled courses
      const { data: discussionsData, error } = await supabase
        .from("course_discussions")
        .select("*, courses!inner(title)")
        .in("course_id", courseIds)
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch authors and reply counts
      const userIds = [...new Set(discussionsData?.map((d) => d.user_id) || [])];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);

      // Get reply counts
      const discussionIds = discussionsData?.map((d) => d.id) || [];
      const { data: replyCounts } = await supabase
        .from("discussion_replies")
        .select("discussion_id")
        .in("discussion_id", discussionIds);

      const replyCountMap = new Map<string, number>();
      replyCounts?.forEach((r) => {
        replyCountMap.set(r.discussion_id, (replyCountMap.get(r.discussion_id) || 0) + 1);
      });

      return discussionsData?.map((d) => ({
        id: d.id,
        user_id: d.user_id,
        title: d.title,
        content: d.content,
        is_pinned: d.is_pinned,
        created_at: d.created_at,
        course_id: d.course_id,
        course_title: (d.courses as { title: string }).title,
        author: profileMap.get(d.user_id) || null,
        reply_count: replyCountMap.get(d.id) || 0,
      })) as DiscussionWithCourse[];
    },
    enabled: !!user?.id,
  });

  if (selectedDiscussion) {
    return (
      <DashboardLayout>
        <DiscussionThread
          discussionId={selectedDiscussion}
          onBack={() => setSelectedDiscussion(null)}
        />
      </DashboardLayout>
    );
  }

  if (selectedCohortDiscussion) {
    return (
      <DashboardLayout>
        <CohortDiscussionThread
          discussionId={selectedCohortDiscussion.id}
          cohortId={selectedCohortDiscussion.cohortId}
          onBack={() => setSelectedCohortDiscussion(null)}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Discussion Forums</h1>
          <p className="text-muted-foreground">
            Join conversations with the community and your courses
          </p>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <TabsList>
            <TabsTrigger value="general" className="gap-2">
              <Users className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="courses" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Course Discussions
            </TabsTrigger>
            <TabsTrigger value="cohorts" className="gap-2">
              <Layers className="h-4 w-4" />
              Cohort
            </TabsTrigger>
            {canSeeMentorTab && (
              <TabsTrigger value="mentors" className="gap-2">
                <Shield className="h-4 w-4" />
                Mentors
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="general" className="mt-6">
            <GeneralDiscussionList />
          </TabsContent>

          <TabsContent value="courses" className="mt-6">
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : discussions && discussions.length > 0 ? (
              <div className="space-y-3">
                {discussions.map((discussion) => (
                  <Card
                    key={discussion.id}
                    className="cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => setSelectedDiscussion(discussion.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <UserAvatar avatarPath={discussion.author?.avatar_url} fullName={discussion.author?.full_name} className="h-10 w-10 flex-shrink-0" fallbackClassName="bg-primary text-primary-foreground" thumbnailSize={64} />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {discussion.is_pinned && (
                                <Pin className="h-4 w-4 text-primary" />
                              )}
                              <h4 className="font-medium truncate">{discussion.title}</h4>
                            </div>
                            <Badge variant="secondary" className="flex-shrink-0">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              {discussion.reply_count}
                            </Badge>
                          </div>

                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {discussion.content}
                          </p>

                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <Link
                              to={`/courses/${discussion.course_id}`}
                              className="flex items-center gap-1 text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <BookOpen className="h-3 w-3" />
                              {discussion.course_title}
                            </Link>
                            <span>•</span>
                            <span><UserName userId={discussion.user_id} name={discussion.author?.full_name} /></span>
                            <span>•</span>
                            <span>
                              {formatDistanceToNow(new Date(discussion.created_at), {
                                addSuffix: true,
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium mb-2">No course discussions yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Enroll in courses and start participating in discussions
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="cohorts" className="mt-6">
            {cohortLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : cohortDiscussions && cohortDiscussions.length > 0 ? (
              <div className="space-y-3">
                {cohortDiscussions.map((discussion) => (
                  <Card
                    key={discussion.id}
                    className="cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() =>
                      setSelectedCohortDiscussion({
                        id: discussion.id,
                        cohortId: discussion.cohort_id,
                      })
                    }
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <UserAvatar avatarPath={discussion.author?.avatar_url} fullName={discussion.author?.full_name} className="h-10 w-10 flex-shrink-0" fallbackClassName="bg-primary text-primary-foreground" thumbnailSize={64} />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {discussion.is_pinned && (
                                <Pin className="h-4 w-4 text-primary" />
                              )}
                              <h4 className="font-medium truncate">{discussion.title}</h4>
                            </div>
                            <Badge variant="secondary" className="flex-shrink-0">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              {discussion.reply_count}
                            </Badge>
                          </div>

                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {discussion.content}
                          </p>

                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className="gap-1">
                              <Layers className="h-3 w-3" />
                              {discussion.cohort_name}
                            </Badge>
                            <span>•</span>
                            <span><UserName userId={discussion.user_id} name={discussion.author?.full_name} /></span>
                            <span>•</span>
                            <span>
                              {formatDistanceToNow(new Date(discussion.created_at), {
                                addSuffix: true,
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium mb-2">No cohort discussions yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Discussions from your cohorts will appear here
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {canSeeMentorTab && (
            <TabsContent value="mentors" className="mt-6">
              <MentorDiscussionList />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
